//Node Modules
import { useEffect, useRef, useCallback } from "react";
import { useSetRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";

//GraphQL
import { API, graphqlOperation, Hub } from "aws-amplify";
import { CONNECTION_STATE_CHANGE, ConnectionState } from "@aws-amplify/pubsub";
import { onCreateLiveEventComms, onDeleteLiveEventComms } from "../../graphql/subscriptions-custom";
import { liveEventCommsByScheme } from "../../graphql/queries-custom";

//Pages

//BinaryForge Components

//3rd Party Components

//Atoms
import { deviceCommsStateAtom, buildCommsStateMap } from "../../atoms/devices";
import { selectedUserSchemeAtom } from "../../atoms/user";
import { tenantAccessibleResourcesSelector } from "../../atoms/tenant";

//Helpers

//Other

//Same safety net as AlarmSubscriber: subscriptions miss events written straight to DynamoDB, and
//events arriving while the websocket is down are gone for good. A slow refetch caps staleness.
const COMMS_POLL_INTERVAL_MS = 60000;
const COMMS_EVENT_TYPE = "OUT_OF_COMMS";
const PAGE_LIMIT = 500;

//Keeps deviceCommsStateAtom holding the scheme's open connection-lost events, which is what the
//portal now means by offline.
const CommsSubscriber = () => {
	//Recoil State
	const setDeviceCommsState = useSetRecoilState(deviceCommsStateAtom);
	const selectedUserScheme = useRecoilValue(selectedUserSchemeAtom);
	const tenantResources = useRecoilValueLoadable(tenantAccessibleResourcesSelector);

	const refreshInFlight = useRef(false);

	const schemeId = selectedUserScheme?.id ?? null;
	const isTenant = selectedUserScheme?.role === "TENANT";
	//null for everyone but a tenant, and while the tenant's own list is still resolving
	const tenantDeviceIds = tenantResources.state === "hasValue" ? (tenantResources.contents?.deviceIds ?? null) : null;

	const getOpenCommsEvents = useCallback(async () => {
		if (!schemeId || refreshInFlight.current) return;
		//A tenant's device list decides what they may see, so there is nothing safe to show until
		//it has resolved.
		if (isTenant && !tenantDeviceIds) return;

		refreshInFlight.current = true;

		try {
			const events = [];
			let nextToken = null;

			do {
				const {
					data: {
						liveEventByScheme: { items, nextToken: newNextToken },
					},
				} = await API.graphql(
					graphqlOperation(liveEventCommsByScheme, {
						schemeId: schemeId,
						filter: { eventType: { eq: COMMS_EVENT_TYPE } },
						limit: PAGE_LIMIT,
						nextToken: nextToken,
					})
				);

				nextToken = newNextToken;
				events.push(...items);
			} while (nextToken !== null);

			const visibleEvents = isTenant
				? events.filter((event) => tenantDeviceIds?.includes(event?.deviceId))
				: events;

			setDeviceCommsState(buildCommsStateMap(visibleEvents));
		} catch (error) {
			console.warn("Connection-lost initial load failed", error);
		} finally {
			refreshInFlight.current = false;
		}
	}, [schemeId, isTenant, tenantDeviceIds, setDeviceCommsState]);

	//A tenant must not be handed offline state for a device they cannot otherwise see
	const isVisibleDevice = useCallback(
		(deviceId) => Boolean(deviceId) && (!isTenant || Boolean(tenantDeviceIds?.includes(deviceId))),
		[isTenant, tenantDeviceIds]
	);

	//Load Initial Data
	useEffect(() => {
		getOpenCommsEvents();
	}, [getOpenCommsEvents]);

	//Setup Subscriber
	useEffect(() => {
		if (!schemeId) return;

		const filter = { schemeId: { eq: schemeId }, eventType: { eq: COMMS_EVENT_TYPE } };

		//eventType is checked again on arrival. If the server-side filter on the enum ever stops
		//matching, the subscription would deliver every live event and every alarm would read as
		//a device going offline.
		const createSubscription = API.graphql(graphqlOperation(onCreateLiveEventComms, { filter })).subscribe({
			next: ({ value }) => {
				const event = value?.data?.onCreateLiveEvent;
				if (event?.eventType !== COMMS_EVENT_TYPE) return;
				if (!isVisibleDevice(event?.deviceId)) return;

				setDeviceCommsState((current) => ({
					...current,
					[event.deviceId]: { offlineSince: event.createdAt ?? null },
				}));
			},
			error: (error) => console.warn("Connection-lost create subscription error", error),
		});

		const deleteSubscription = API.graphql(graphqlOperation(onDeleteLiveEventComms, { filter })).subscribe({
			next: ({ value }) => {
				const event = value?.data?.onDeleteLiveEvent;
				if (event?.eventType !== COMMS_EVENT_TYPE || !event?.deviceId) return;

				setDeviceCommsState((current) => {
					if (!(event.deviceId in current)) return current;
					const next = { ...current };
					delete next[event.deviceId];
					return next;
				});
			},
			error: (error) => console.warn("Connection-lost delete subscription error", error),
		});

		return () => {
			if (createSubscription?.unsubscribe) createSubscription.unsubscribe();
			if (deleteSubscription?.unsubscribe) deleteSubscription.unsubscribe();
		};
		//Subscribe once per scheme. Re-subscribing whenever the map changes would lose events
		//arriving in the teardown and setup gap, which is how a device gets stuck showing offline
		//after it has already come back.
	}, [schemeId, isVisibleDevice, setDeviceCommsState]);

	//Polling safety net
	useEffect(() => {
		if (!schemeId) return;

		const interval = setInterval(() => getOpenCommsEvents(), COMMS_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [schemeId, getOpenCommsEvents]);

	//Events missed while the websocket was down are lost - refetch when the connection recovers
	useEffect(() => {
		let lastState = null;
		const stopListening = Hub.listen("api", ({ payload }) => {
			if (payload.event !== CONNECTION_STATE_CHANGE) return;
			const state = payload.data.connectionState;
			if (state === ConnectionState.Connected && lastState && lastState !== ConnectionState.Connected) {
				getOpenCommsEvents();
			}
			lastState = state;
		});

		return () => stopListening();
	}, [getOpenCommsEvents]);

	return null;
};

export default CommsSubscriber;
